import {
  Component,
  ChangeDetectionStrategy,
  input,
  model,
  signal,
  computed,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';

@Component({
  selector: 'app-filter-panel',
  imports: [TranslateModule, MatCheckboxModule, MatRadioModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host {
      display: block;

      /* Material checkbox theming */
      --mdc-checkbox-selected-checkmark-color: #fff;
      --mdc-checkbox-selected-icon-color: var(--color-mty-primary);
      --mdc-checkbox-selected-hover-icon-color: var(--color-mty-primary);
      --mdc-checkbox-selected-focus-icon-color: var(--color-mty-primary);
      --mdc-checkbox-selected-pressed-icon-color: var(--color-mty-primary);
      --mdc-checkbox-unselected-hover-icon-color: var(--color-mty-primary);

      /* Compact checkbox: shrink 40px touch target to 24px */
      --mdc-checkbox-state-layer-size: 24px;
      --mdc-checkbox-selected-hover-state-layer-opacity: 0;
      --mdc-checkbox-selected-focus-state-layer-opacity: 0;
      --mdc-checkbox-selected-pressed-state-layer-opacity: 0;
      --mdc-checkbox-unselected-hover-state-layer-opacity: 0;
      --mdc-checkbox-unselected-focus-state-layer-opacity: 0;
      --mdc-checkbox-unselected-pressed-state-layer-opacity: 0;

      /* Material radio theming */
      --mdc-radio-selected-icon-color: var(--color-mty-primary);
      --mdc-radio-selected-hover-icon-color: var(--color-mty-primary);
      --mdc-radio-selected-focus-icon-color: var(--color-mty-primary);
      --mdc-radio-selected-pressed-icon-color: var(--color-mty-primary);
      --mdc-radio-unselected-hover-icon-color: var(--color-mty-primary);

      /* Hide radio state layer (hover/focus circle) */
      --mat-radio-ripple-color: transparent;
      --mat-radio-checked-ripple-color: transparent;
      --mdc-radio-state-layer-size: 24px;

      /* Label font size */
      --mdc-form-field-label-text-size: 0.875rem;
    }
  `],
  template: `
    <div class="mb-3">
      <h5 class="font-alternate text-sm font-semibold text-black tracking-tight mb-1.5">
        {{ title() | translate }}
      </h5>

      @if (description()) {
        <p class="text-xs text-gray-400 leading-relaxed mb-3">{{ description() | translate }}</p>
      }

      <!-- Tag pills -->
      @if (tags().length) {
        <div class="flex flex-wrap gap-1.5 mb-3">
          @for (tag of tags(); track tag.name) {
            <button
              type="button"
              class="text-xs px-2.5 py-1 rounded-full border cursor-pointer select-none transition-all duration-100 active:scale-95"
              [class]="activeTags().has(tag.name!)
                ? 'bg-mty-primary text-white border-mty-primary'
                : 'bg-white text-gray-500 border-gray-200 hover:border-mty-primary hover:text-mty-primary'"
              (click)="toggleTag(tag.name!)">
              {{ tag.name }}<span class="opacity-50 ml-0.5">({{ countByTag(tag.name!) }})</span>
            </button>
          }
        </div>
      }

      <!-- CHECKBOX -->
      @if (type() === 'checkbox') {
        <div class="flex flex-col -space-y-px">
          @for (item of displayedOptions(); track item[valueKey()]) {
            <div class="group">
              <div class="flex items-center">
                <mat-checkbox disableRipple
                  [checked]="isChecked(item[valueKey()])"
                  (change)="toggleCheckbox(item[valueKey()])">
                  <span class="text-sm text-gray-700 group-hover:text-black transition-colors">
                    {{ item[labelKey()] | translate }}
                  </span>
                </mat-checkbox>

                @if (item.children?.length) {
                  <button type="button"
                          class="ml-auto p-1 text-gray-300 hover:text-mty-primary transition-colors cursor-pointer bg-transparent border-0"
                          (click)="toggleExpand(item[valueKey()])">
                    <svg class="w-3.5 h-3.5 transition-transform duration-200"
                         [class.rotate-180]="expanded().has(item[valueKey()])"
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                }
              </div>

              @if (item.children?.length) {
                <div class="ml-6 pl-3 border-l border-gray-100 grid transition-[grid-template-rows] duration-200 ease-in-out"
                     [class]="expanded().has(item[valueKey()]) ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'">
                  <div class="overflow-hidden">
                    @for (child of item.children; track child[valueKey()]) {
                      <mat-checkbox disableRipple
                        [checked]="isChecked(child[valueKey()])"
                        (change)="toggleCheckbox(child[valueKey()])">
                        <span class="text-sm text-gray-600">{{ child[labelKey()] | translate }}</span>
                      </mat-checkbox>
                    }
                  </div>
                </div>
              }
            </div>
          }

          @if (hasMore()) {
            <button type="button"
                    class="mt-2 text-xs font-medium text-mty-primary hover:text-mty-primary-light flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0 transition-colors duration-150"
                    (click)="collapsed.set(!collapsed())">
              {{ (collapsed() ? 'Show more' : 'Show less') | translate }}
              <svg class="w-3 h-3 transition-transform duration-200"
                   [class.rotate-180]="!collapsed()"
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>
          }
        </div>
      }

      <!-- RADIO -->
      @if (type() === 'radio') {
        <mat-radio-group disableRipple
          [value]="value()"
          (change)="toggleRadio($event.value)"
          class="flex flex-col -space-y-px">
          @for (item of displayedOptions(); track item[valueKey()]) {
            <div class="group">
              <div class="flex items-center">
                <mat-radio-button disableRipple [value]="item[valueKey()]">
                  <span class="text-sm text-gray-700 group-hover:text-black transition-colors">
                    {{ item[labelKey()] | translate }}
                  </span>
                </mat-radio-button>

                @if (item.children?.length) {
                  <button type="button"
                          class="ml-auto p-1 text-gray-300 hover:text-mty-primary transition-colors cursor-pointer bg-transparent border-0"
                          (click)="toggleExpand(item[valueKey()])">
                    <svg class="w-3.5 h-3.5 transition-transform duration-200"
                         [class.rotate-180]="expanded().has(item[valueKey()])"
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                }
              </div>

              @if (item.children?.length) {
                <div class="ml-6 pl-3 border-l border-gray-100 grid transition-[grid-template-rows] duration-200 ease-in-out"
                     [class]="expanded().has(item[valueKey()]) ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'">
                  <div class="overflow-hidden">
                    @for (child of item.children; track child[valueKey()]) {
                      <mat-radio-button disableRipple [value]="child[valueKey()]">
                        <span class="text-sm text-gray-600">{{ child[labelKey()] | translate }}</span>
                      </mat-radio-button>
                    }
                  </div>
                </div>
              }
            </div>
          }

          @if (hasMore()) {
            <button type="button"
                    class="mt-2 text-xs font-medium text-mty-primary hover:text-mty-primary-light flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0 transition-colors duration-150"
                    (click)="collapsed.set(!collapsed())">
              {{ (collapsed() ? 'Show more' : 'Show less') | translate }}
              <svg class="w-3 h-3 transition-transform duration-200"
                   [class.rotate-180]="!collapsed()"
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>
          }
        </mat-radio-group>
      }
    </div>
  `,
})
export class FilterPanelComponent {
  // Config
  title = input('');
  description = input('');
  type = input<'checkbox' | 'radio'>('checkbox');
  options = input<any[]>([]);
  tags = input<{ id?: string; name?: string | null }[]>([]);
  limit = input(10);
  valueKey = input('value');
  labelKey = input('label');

  // Two-way binding
  value = model<any>(null);

  // Internal state
  collapsed = signal(true);
  expanded = signal(new Set<string>());
  activeTags = signal(new Set<string>());

  // Computed
  private filteredOptions = computed(() => {
    const active = this.activeTags();
    const all = this.options();
    if (!this.tags().length || active.size === 0) return all;
    return all.filter((opt: any) => opt.tags?.some((t: string) => active.has(t)));
  });

  displayedOptions = computed(() => {
    const opts = this.filteredOptions();
    return this.collapsed() ? opts.slice(0, this.limit()) : opts;
  });

  hasMore = computed(() => this.filteredOptions().length > this.limit());

  // Checkbox
  isChecked(val: any): boolean {
    const v = this.value();
    return Array.isArray(v) && v.includes(val);
  }

  toggleCheckbox(val: any): void {
    const current = this.value() ?? [];
    this.value.set(
      this.isChecked(val)
        ? current.filter((v: any) => v !== val)
        : [...current, val]
    );
  }

  // Radio (toggle-off: clicking the selected value deselects it)
  toggleRadio(val: any): void {
    this.value.set(this.value() === val ? null : val);
  }

  // Tags
  toggleTag(name: string): void {
    this.activeTags.update(set => {
      const next = new Set(set);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
    this.collapsed.set(true);
  }

  countByTag(name: string): number {
    return this.options().filter((o: any) => o.tags?.includes(name)).length;
  }

  // Tree
  toggleExpand(val: string): void {
    this.expanded.update(set => {
      const next = new Set(set);
      next.has(val) ? next.delete(val) : next.add(val);
      return next;
    });
  }
}
